<ng-container *ngIf="portalOrder$ | async as portalOrder">
  @let isWithStripe = portalOrder.acceptStripe && withStripe;

  <stepper
    [step]="Steps.OverviewAndPayment"
    [isChangeMode]="portalOrder.isChange"
    [ancillaryUpsell]="ancillaryUpsell"
    [tiers]="portalTiers"
    [perpetual]="state.perpetual"
  />

  <div class="my-4 my-md-6 d-flex flex-column flex-md-row">
    <div class="d-flex flex-column flex-1 overview-and-payment">
      <div>
        <h2 class="semi-bold mx-4 mx-md-0">Trip summary</h2>
        @if (referralCreditPreview && !referralCreditPreview.applies) {
          <referral-credit-not-applied-banner
            [reason]="referralCreditNotAppliedReason"
            class="mx-4 mx-md-0 mt-4 d-block overview-and-payment-block"
          />
        }
        @if (ancillaryUpsell) {
          <upsell-details
            [flightItineraries]="portalOrder.flightItineraries"
            class="mx-4 mx-md-0 mt-4"
          />
        } @else {
          <portal-option-details
            [itineraries]="portalOrder.flightItineraries"
            [portalLink]="state.portalLink"
            class="option-details"
          />
        }
      </div>
      @if (portalOrder.hotelBookings && portalOrder.hotelBookings.length) {
        <hotel-bookings [hotelBookings]="portalOrder.hotelBookings" class="w-100 hotel-bookings" />
      }
      @if (!portalOrder.isChange && state.passengers.length) {
        <passengers-preview [passengers]="state.passengers" (editClick)="navigateToTravelers()" />
      }
      @if (isDisruptionProtection || isCancelForAnyReason) {
        <protection-plans
          [ancillaryUpsell]="ancillaryUpsell"
          [premiumDisruptionAssistance]="isDisruptionProtection"
          [cancelForAnyReason]="isCancelForAnyReason"
          (editClick)="navigateToPlans()"
        />
      }
      @if (!isFree) {
        @if (isWithStripe) {
          <stripe-form
            (valueChange)="valueChange()"
            (formSubmit)="stripeFormSubmit($event)"
            (stripeFormSubmitCallback)="stripeFormSubmitCallback()"
            (setReadAndAcceptError)="readAndAcceptError = $event"
            (back)="tryBack()"
            [errors]="errors"
            [creditCards]="stripeCreditCards"
            [readAndAccept]="readAndAccept"
            [clientBalanceAmount]="clientBalanceAmount"
            [maxBalanceAmount]="
              portalOrder.maxBalanceAmount
                | getMaxBalanceAmount: clientBalanceAmount : state : portalOrder
            "
            [totalAmount]="
              portalOrder.soldAmount | getTotalAmount: state : portalOrder : referralCreditDiscount
            "
            [clientSecret]="clientSecret"
            class="mt-md-8"
          >
            @if (!ancillaryUpsell) {
              <ng-container *ngTemplateOutlet="promocodeTemplate" />
            }
            <ng-container *ngTemplateOutlet="termsTemplate" />
          </stripe-form>
        } @else {
          <card-form
            (valueChange)="valueChange()"
            (formSubmit)="formSubmit($event)"
            (setReadAndAcceptError)="readAndAcceptError = $event"
            (back)="tryBack()"
            [model]="model"
            [errors]="errors"
            [creditCards]="creditCards"
            [readAndAccept]="readAndAccept"
            [clientBalanceAmount]="clientBalanceAmount"
            [maxBalanceAmount]="
              portalOrder.maxBalanceAmount
                | getMaxBalanceAmount: clientBalanceAmount : state : portalOrder
            "
            [totalAmount]="
              portalOrder.soldAmount | getTotalAmount: state : portalOrder : referralCreditDiscount
            "
          >
            @if (!ancillaryUpsell) {
              <ng-container *ngTemplateOutlet="promocodeTemplate" />
            }
            <ng-container *ngTemplateOutlet="termsTemplate" />
          </card-form>
        }
      } @else {
        <div>
          <ng-container *ngTemplateOutlet="termsTemplate; context: { isStandalone: true }" />
          <div
            class="mt-4 mx-4 mx-md-0 mr-4 ml-4 mr-md-0 ml-md-0 d-flex flex-column-reverse flex-md-row d-md-none"
          >
            <ds-button
              (click)="tryBack()"
              variant="secondary"
              leadingIcon="chevronLeft"
              [fullWidth]="true"
              class="mt-4 mt-md-0 w-md-unset"
              data-id="back"
              label="Back"
            />

            <ds-button
              (click)="submitFreeChanges()"
              [fullWidth]="true"
              class="ml-md-auto w-md-unset"
              data-id="submit"
              label="Confirm and Pay"
            />
          </div>
        </div>
      }
      <ng-template #promocodeTemplate>
        <div promocode class="overview-and-payment-block">
          <h2 class="semi-bold mb-4 mx-4 mx-md-0">Promo code</h2>

          <promocode
            (applyPromocode)="applyPromocode($event)"
            (removePromocode)="removePromocode()"
            [promocode]="state.promocode"
            [errors]="errors"
          />
        </div>
      </ng-template>

      <ng-template let-isStandalone="isStandalone" #termsTemplate>
        <div terms class="overview-and-payment-block" [class.standalone]="isStandalone">
          <term-of-use
            (selectReadAndAccept)="selectReadAndAccept($event)"
            [disclaimersHtml]="
              state.disruptionProtection || state.cancelForAnyReason
                ? portalOrder.disclaimersWithAdditionalProductsHtml
                : portalOrder.disclaimersHtml
            "
            [disclaimersCheckmarkHtml]="portalOrder.disclaimersCheckmarkHtml"
            [readAndAccept]="readAndAccept"
            [readAndAcceptError]="readAndAcceptError"
          />
        </div>
        <div class="d-md-none mt-2 p-4">
          <ng-container *ngTemplateOutlet="summaryBlock" />
        </div>
      </ng-template>
    </div>

    <div class="d-none d-md-block ml-md-4 checkout-summary">
      <ng-container *ngTemplateOutlet="summaryBlock" />
    </div>
  </div>

  <ng-template #summaryBlock>
    <price-summary
      [option]="portalOrder"
      [tier]="state.tier"
      [disruptionProtection]="state.disruptionProtection"
      [tipsAmount]="state.tipsAmount"
      [cancelForAnyReason]="state.cancelForAnyReason"
      [promocodeDiscountAmount]="state.promocodeDiscountAmount"
      [referralCreditDiscount]="referralCreditDiscount"
      [bundle]="state.bundle"
      [actions]="summaryActions"
      (actionClick)="onSummaryAction($event)"
    />
  </ng-template>
</ng-container>

<ng-template #confirmTemplate>
  <go-back-confirm (back)="back()" />
</ng-template>
